import { ConfigurableColumns } from '@/components/ConfigurableColumns';
import { CopiableIdDataGridField } from '@/components/CopiableIdField';
import { GenericDateFunctionField } from '@/components/GenericDateFunctionField';
import { Chip, type ChipProps } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import { DateField, FunctionField } from 'react-admin';

import { isManualSessionNoteProcessingFrom } from './processingFromSemantics';

const payStatusChipColor = (status: string): ChipProps['color'] => {
  switch (status) {
    case 'customer':
    case 'reactivated':
    case 'free-subscription':
      return 'success';
    case 'trialing':
      return 'info';
    case 'lifelong-trial':
      return 'secondary';
    case 'trial-completed':
    case 'pending-cancellation':
      return 'warning';
    case 'billing-issues':
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

export const columns = (
  hideDeleted: boolean,
): ConfigurableColumns[] => [
  // Default visible columns in the requested order
  {
    label: 'Note Date',
    source: 'createdAt',
    GridComponent: DateField,
    sortable: true,
    exportFn: (doc: any) =>
      doc.createdAt ? new Date(doc.createdAt).toISOString() : '',
  },
  {
    label: 'Note Time',
    source: 'createdAtTime',
    key: 'createdAtTime',
    sortBy: 'createdAt',
    sortable: true,
    GridComponent: GenericDateFunctionField,
    gridComponentProps: { format: 'HH:mm:ss', valueSource: 'createdAt' },
    exportFn: false,
  },
  { label: 'App Version', source: 'appVersion' },
  {
    label: 'First Name',
    sortable: false,
    source: 'therapistFirstName',
  },
  {
    label: 'Last Name',
    sortable: false,
    source: 'therapistLastName',
  },
  { label: 'Email', sortable: false, source: 'therapistEmail' },
  {
    label: 'Therapist Id',
    source: 'therapistId',
    exportFn: record => record.therapistId,
    sortable: false,
    GridComponent: CopiableIdDataGridField,
    gridComponentProps: {
      targetNavigationResource: 'therapists',
      referenceField: '_id',
      filter: ['therapistId', 'therapistId'],
      targetNavigationFilters: {
        isHiddenTestAccount: 'visible',
      },
    },
  },
  {
    label: 'Note Type',
    source: 'live',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => (
        <Chip
          label={record.live ? 'Live' : 'Dictation'}
          color={record.live ? 'primary' : 'secondary'}
        />
      ),
    },
  },
  {
    label: 'Sample Note',
    source: 'isSampleNote',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => (
        <Chip
          label={record.isSampleNote ? 'Yes' : 'No'}
          color={record.isSampleNote ? 'success' : 'default'}
        />
      ),
    },
  },
  {
    label: 'Narrative Word Count',
    source: 'wordCount.narrative',
  },
  { label: 'Framework', source: 'subType' },
  {
    label: 'Pay Status',
    source: 'payStatus',
    sortable: false,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => {
        const raw = record.payStatus;
        if (raw == null || raw === '') {
          return null;
        }
        const label = String(raw);
        return (
          <Chip
            label={label}
            color={payStatusChipColor(label)}
            size="small"
          />
        );
      },
    },
    exportFn: (record: any) => record.payStatus ?? '',
  },
  { label: 'Status', source: 'status' },
  // Hidden columns (omit: true)
  {
    label: 'Note Id',
    omit: true,
    source: 'id',
    sortable: true,
    exportFn: (doc: any) => doc.id,
  },
  {
    label: 'Timezone',
    source: 'deviceInfo.timezone.offset',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.deviceInfo?.timezone ? (
          <Chip
            label={`${record.deviceInfo.timezone.name} (${record.deviceInfo.timezone.gmt})`}
          />
        ) : null,
    },
    omit: true,
  },
  {
    label: 'Title',
    source: 'therapistTitle',
    sortable: false,
    omit: true,
  },
  {
    label: 'Client Id',
    sortable: false,
    source: 'patientId',
    GridComponent: CopiableIdDataGridField,
    omit: true,
  },
  {
    label: 'Processing Type',
    sortable: false,
    source: 'processingHistory.from',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => {
        if (record.status !== 'processed') {
          return null;
        }

        const hasManualProcessing = record.processingHistory?.some(
          (ph: { from?: string | null }) =>
            isManualSessionNoteProcessingFrom(ph.from),
        );

        return (
          <Chip
            label={hasManualProcessing ? 'Manual' : 'Automatic'}
            color={hasManualProcessing ? 'warning' : 'info'}
          />
        );
      },
    },
    exportFn: (record: any) => {
      if (record.status !== 'processed') {
        return null;
      }

      return record.processingHistory?.some((ph: { from?: string | null }) =>
        isManualSessionNoteProcessingFrom(ph.from),
      )
        ? 'Manual'
        : 'Automatic';
    },
    omit: true,
  },
  { label: 'Files Count', sortable: false, source: 'filesCount', omit: true },
  {
    label: 'Media Type',
    source: 'mediaType',
    GridComponent: FunctionField,
    sortable: false,
    gridComponentProps: {
      render: (record: any) => (
        <Chip
          icon={
            record.mediaType === 'text' ? (
              <EditIcon />
            ) : record.mediaType === 'audio' ? (
              <MicIcon />
            ) : undefined
          }
          color={
            record.mediaType === 'text'
              ? 'success'
              : record.mediaType === 'audio'
                ? 'info'
                : 'default'
          }
          size="small"
          sx={{
            color: 'white',
            padding: '12px',
            backgroundColor:
              record.mediaType === 'text' ? '#4caf50' : undefined,
            '& .MuiChip-icon': {
              color: 'white',
              margin: 0,
              fontSize: '1.2rem',
            },
            '& .MuiChip-label': {
              display: 'none',
            },
          }}
        />
      ),
    },
    omit: true,
  },
  { label: 'App Fingerprint', source: 'appFingerprint', omit: true },
  {
    label: 'Deleted At',
    source: 'deletedAt',
    omit: true,
    GridComponent: GenericDateFunctionField,
  },
];
